/**
 * What is for sale.
 *
 * Big tiles in a grid, because this is tapped hundreds of times a shift by
 * someone who is not looking closely. The name wraps rather than truncating —
 * "Chicken Tikka…" and "Chicken Tikka Pizza" are different orders, and a
 * cashier who has to tap to find out which is which is slower every time.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { OpenOrders, useOpenOrders } from './OpenOrders.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { thumbUrl } from '../../lib/images.js';

export function ProductGrid({ onPick, onSettle, onBill, onAddMore, settling }) {
  /**
   * `null` is All, an id is one category, and 'open' is the board of orders
   * still on. Kept as one piece of state rather than a separate flag, because
   * two states that must never both be true is how a screen ends up showing
   * two things at once.
   */
  const [category, setCategory] = useState(null);
  const open = useOpenOrders();
  const openCount = open.data?.count ?? 0;
  const [search, setSearch] = useState('');

  const categories = useQuery({
    queryKey: ['menu', 'categories'],
    queryFn: () => api.get('/menu/categories'),
    staleTime: 5 * 60_000,
  });

  const products = useQuery({
    queryKey: ['menu', 'products'],
    // Everything at once: a restaurant menu is small, and paging it would put
    // a network round trip between a cashier and the next tap.
    //
    // The choices each dish offers come down with it, for the same reason and
    // one more: the till has to know whether to ask a question the moment a
    // dish is tapped, and it has to know that while offline.
    queryFn: () =>
      api.get(
        withQuery('/menu/products', { limit: 500, is_active: true, with_modifiers: 1 })
      ),
    staleTime: 5 * 60_000,
  });

  const visible = useMemo(() => {
    const all = products.data?.products ?? [];
    const term = search.trim().toLowerCase();

    return all.filter((p) => {
      if (category && p.category_id !== category) return false;
      if (term && !p.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [products.data, category, search]);

  if (products.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner label="Loading the menu" />
      </div>
    );
  }

  if (products.isError) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-ink-2">
          Could not load the menu. Check the branch computer is running.
        </p>
      </div>
    );
  }

  const categoryList = categories.data?.categories ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-rule bg-surface p-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the menu"
          aria-label="Search the menu"
          className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-[15px] placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />

        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5">
          {/*
            First, before the food.

            What is on at the moment is the thing a cashier looks at when they
            walk up to the till, and the thing they need when somebody comes to
            pay. Behind a button it would be one tap further away than the menu,
            which has it exactly backwards.
          */}
          {onSettle && (
            <CategoryChip active={category === 'open'} onClick={() => setCategory('open')}>
              On now
              {openCount > 0 && (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs tabular-nums ${
                    category === 'open' ? 'bg-surface-2/25' : 'bg-brand-400/20 text-brand-300'
                  }`}
                >
                  {openCount}
                </span>
              )}
            </CategoryChip>
          )}

          <CategoryChip active={category === null} onClick={() => setCategory(null)}>
            All
          </CategoryChip>
          {categoryList.map((c) => (
            <CategoryChip key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
              {c.name}
            </CategoryChip>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {category === 'open' ? (
          <OpenOrders
            onSettle={onSettle}
            onBill={onBill}
            settling={settling}
            onAddMore={
              onAddMore &&
              // Adding starts from the board but happens in the menu, so leave
              // the board and show the dishes to pick from.
              ((order) => {
                onAddMore(order);
                setCategory(null);
              })
            }
          />
        ) : visible.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-2">
            {search ? `Nothing matching “${search}”.` : 'Nothing in this category.'}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {visible.map((product) => {
              const asks = products.data?.modifierGroups?.[product.id] ?? [];

              return (
                <button
                  key={product.id}
                  onClick={() => onPick(product, asks)}
                  className="flex flex-col overflow-hidden rounded-xl border border-rule bg-surface text-left transition-colors hover:border-brand-400 hover:bg-brand-400/10 active:bg-brand-400/20"
                >
                  {/*
                    A photo helps a new cashier find the dish without reading,
                    and helps everyone tell two similar names apart. Dishes
                    with no photo yet show a plain tile rather than a broken
                    image — a menu is added to over time, not all at once.
                  */}
                  <div className="flex aspect-[16/10] w-full items-center justify-center bg-surface-3">
                    {product.image_url ? (
                      <img
                        src={thumbUrl(product.image_url)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span aria-hidden="true" className="text-2xl text-ink-3/40">
                        🍽
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col justify-between gap-0.5 p-2">
                    <div className="space-y-0.5">
                      <span className="line-clamp-2 block text-[13px] font-medium leading-snug text-ink">
                        {product.name}
                      </span>
                      {/*
                        What the dish contains — a platter's parts, say — shown
                        only when there is one, so an ordinary dish keeps the
                        smaller tile it had.
                      */}
                      {product.description && (
                        <span className="line-clamp-2 block text-[11px] leading-snug text-ink-3">
                          {product.description}
                        </span>
                      )}
                    </div>
                    <span className="flex items-baseline justify-between gap-1.5">
                      <span className="text-[13px] font-semibold text-brand-700 tabular-nums">
                        {formatPKR(product.price)}
                      </span>
                      {/*
                        Marked so the tap is not a surprise. A dish that opens a
                        sheet and one that drops straight into the basket behave
                        differently, and a cashier working at speed should be
                        able to see which is which before touching it.
                      */}
                      {asks.length > 0 && (
                        <span className="text-[11px] text-ink-3">
                          {asks.length === 1 ? '1 choice' : `${asks.length} choices`}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChip({ active, children, ...props }) {
  return (
    <button
      aria-pressed={active}
      className={`h-10 shrink-0 rounded-lg px-3.5 text-sm font-medium transition-colors ${
        active ? 'bg-brand-400 text-surface-2' : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
      }`}
      {...props}
    >
      {children}
    </button>
  );
}
